// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { SectionComponent, GSGrid, Button, ButtonGroup, Icons, StatItem, View } from "@components";
import { compose } from "redux";

import { AbsenceReviewViewModal } from "@modules/shared/components";
import { formatDate } from "@transformers/date";
import actionCreator from "@modules/shared/actionCreator";
import reportingActionCreator from "@modules/reporting/actionCreator";
import * as selectors from "@modules/shared/selectors";
import * as employeeSelectors from "@modules/employee/selectors";
import { getSelectedTaskHistory } from "@modules/reporting/selectors/tasks";

import { formatAuthoredBy } from "@transformers/names";
import { MonitoringUpdateModal, ManualReferralModal } from "@modules/shared/components";
import type {
  EventHistoryItem,
  EventHistoryButtonTypes,
  ManualReferralTaskData,
} from "@modules/shared/reducer/employee-events/types";
import type {
  AbsenceReviewTask,
  ReferralDecisionTask,
} from "@modules/shared/reducer/absence-review/types";
import type { State } from "@store/reducers";
import { push } from "connected-react-router";
import { AbsenceRTWInterviewModal, ReferralDecisionModal } from "@modules/shared/components";
import type { RtwInterviewHistory } from "@modules/shared/reducer/employee-absences/types";

import { ManualReferralTaskModal } from "@modules/shared/components/index";

type OwnProps = $Exact<{
  employeeId: number,
  clientId: number,
  showSummary: boolean,
  enableMonitoringUpdate: boolean,
  enableManualReferral: boolean,
  gridSectionTitle: string,
  hideFilters: boolean,
  filters: Array<EventHistoryButtonTypes>,
  defaultFilter: EventHistoryButtonTypes,
}>;

type PropsFromState = $Exact<{
  eventHistoryList: Object,
  lastAbsenceReviewTask: ?AbsenceReviewTask,
  selectedAbsenceReviewTask: ?AbsenceReviewTask,
  selectReferralDecisionTaskData: ?ReferralDecisionTask,
  selectedRTWTask: ?RtwInterviewHistory,
  showUpdateModal: boolean,
  showManualReferralModal: boolean,
  isManualReferralAvailable: boolean,
  showHistoryModal: boolean,
  showAbsenceReviewModal: boolean,
  showReferralDecisionModal: boolean,
  showManualReferralTaskData: boolean,
  manualReferralTaskData: ManualReferralTaskData,
  currentEmployeeAccessLevel: string,
  task: ?AbsenceReviewTask,
}>;

type PropsFromDispatch = $Exact<{
  getEventHistory: typeof actionCreator.GetEmployeeEventHistory,
  getManualReferralEnabled: typeof actionCreator.GetManualReferralEnabled,
  getReferralProcesses: typeof actionCreator.GetReferralProcesses,
  getLastAbsenceReviewTask: typeof actionCreator.GetLastAbsenceReviewTask,
  getAbsenceReviewTask: typeof actionCreator.GetAbsenceReviewTask,
  getReferralDecisionTaskData: typeof actionCreator.GetReferralDecisionTaskData,
  getManualReferralTaskData: typeof actionCreator.GetManualReferralTaskData,
  getRtwInterviewTask: typeof reportingActionCreator.GetRtwInterviewTask,
  setRtwInterviewTaskHistory: typeof reportingActionCreator.SetRtwInterviewTaskHistory,
  toggleUpdateModal: typeof actionCreator.ToggleMonitoringUpdateModal,
  toggleManualReferralModal: typeof actionCreator.ToggleManualReferralModal,
  toggleHistoryModal: typeof actionCreator.ToggleEventHistoryItemModal,
  toggleAbsenceReviewModal: typeof actionCreator.ToggleAbsenceReviewModal,
  toggleReferralDecisionModal: typeof actionCreator.ToggleReferralDecisionModal,
  toggleManualReferralTaskDataModal: typeof actionCreator.ToggleManualReferralTaskDataModal,
  resetHistory: typeof actionCreator.ResetEmployeeEventHistory,
  updateReviewTask: typeof actionCreator.GetLastAbsenceReviewTaskSuccess,
  push: typeof push,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  activeButton: EventHistoryButtonTypes,
}>;

export class EventHistory extends React.Component<Props, ComponentState> {
  state = {
    activeButton: this.props.defaultFilter || "ALL",
  };

  componentDidMount() {
    this.props.getEventHistory({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      filters: { taskType: this.state.activeButton },
    });

    this.props.enableManualReferral &&
      this.props.getManualReferralEnabled({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
      });
    this.props.getReferralProcesses({
      clientId: this.props.clientId,
    });
  }
  componentDidUpdate(prevProps: Props) {
    if (
      this.props.eventHistoryList &&
      JSON.stringify(this.props.eventHistoryList) !== JSON.stringify(prevProps.eventHistoryList)
    ) {
      const lastAbsenceReviewTask =
        this.historyList &&
        this.historyList.find(item => item.taskType === "ABSENCE_MONITORING_REVIEW");

      if (lastAbsenceReviewTask) {
        this.props.getLastAbsenceReviewTask({
          clientId: this.props.clientId,
          taskId: lastAbsenceReviewTask.id,
        });
      }
    }

    if (!this.props.task && prevProps.task) {
      this.props.getEventHistory({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
        filters: { taskType: this.state.activeButton },
      });
    }
  }
  getEventHistory = (buttonType: EventHistoryButtonTypes) => () => {
    this.setState({
      activeButton: buttonType,
    });

    const payload = {
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      filters: buttonType !== "ALL" ? { taskType: buttonType } : undefined,
    };
    this.props.getEventHistory(payload);
  };
  getButtonState = (buttonType: EventHistoryButtonTypes) => {
    return this.state.activeButton === buttonType ? "selected" : null;
  };
  toggleUpdateModal = () => {
    this.props.toggleUpdateModal(!this.props.showUpdateModal);
  };
  toggleHistoryModal = (item?: EventHistoryItem) => () => {
    if (item) {
      this.props.getRtwInterviewTask({
        clientId: this.props.clientId,
        taskId: item.id,
      });
    }

    this.props.setRtwInterviewTaskHistory(null);
    this.props.toggleHistoryModal(!this.props.showHistoryModal);
  };
  toggleAbsenceReviewTaskModal = (item?: EventHistoryItem) => {
    if (item) {
      this.props.getAbsenceReviewTask({
        clientId: this.props.clientId,
        taskId: item.id,
      });
    }

    this.props.toggleAbsenceReviewModal(!this.props.showAbsenceReviewModal);
  };
  toggleEmployeeReferralDecision = (item?: EventHistoryItem) => {
    if (item) {
      this.props.getReferralDecisionTaskData({
        clientId: this.props.clientId,
        taskId: item.id,
        referralType: "employee",
      });
      this.props.toggleReferralDecisionModal();
    }
  };
  toggleMangerReferralDecision = (item?: EventHistoryItem) => {
    if (item) {
      this.props.getReferralDecisionTaskData({
        clientId: this.props.clientId,
        taskId: item.id,
        referralType: "manager",
      });
      this.props.toggleReferralDecisionModal();
    }
  };
  toggleManualReferralTask = (item?: EventHistoryItem) => {
    if (item) {
      this.props.getManualReferralTaskData({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
        taskId: item.id,
      });
    }
  };
  handleTaskClick = (item: EventHistoryItem) => () => {
    switch (item.taskType) {
      case "ABSENCE_MONITORING_REVIEW":
        this.toggleAbsenceReviewTaskModal(item);
        break;

      case "EMPLOYEE_REFERRAL_DECISION":
        this.toggleEmployeeReferralDecision(item);
        break;

      case "MANAGER_REFERRAL_DECISION":
        this.toggleMangerReferralDecision(item);
        break;

      case "MANUAL_REFERRAL":
        this.toggleManualReferralTask(item);
        break;

      default:
        this.toggleHistoryModal(item)();
        break;
    }
  };
  get filters() {
    return (
      this.props.filters || [
        "ALL",
        "ABSENCE_MONITORING_REVIEW",
        "RTW_INTERVIEW",
        "ALERT_TRIGGERED",
        "REFERRALS",
      ]
    );
  }
  get isValid() {
    return this.props.lastAbsenceReviewTask && this.props.lastAbsenceReviewTask.currentLevel;
  }

  get selectedRTWTask(): RtwInterviewHistory {
    return {
      completedBy: (this.props.selectedRTWTask || {}).completedBy,
      completionDate: (this.props.selectedRTWTask || { completionDate: "" }).completionDate,
      questionAnswers: (this.props.selectedRTWTask || { questionAnswers: [] }).questionAnswers,
      hasExpired: false,
      type: (this.props.selectedRTWTask || {}).type,
      id: (this.props.selectedRTWTask || {}).id,
      dueDate: this.props.selectedRTWTask.dueDate,
      rtwAt: this.props.selectedRTWTask.rtwAt,
    };
  }

  get historyList() {
    return this.props.eventHistoryList[this.state.activeButton].content;
  }
  get columns() {
    return [
      {
        key: "id",
        name: this.props.t("ACTIVITY_ID"),
      },
      {
        key: "taskType",
        name: this.props.t("TASK_TYPE"),
        renderCell: ({ row }: { row: EventHistoryItem }) => this.props.t(row.taskType),
      },
      {
        key: "description",
        name: this.props.t("DESCRIPTION"),
      },
      {
        key: "createdAt",
        name: this.props.t("ACTIVITY_DATE"),
        renderCell: ({ row }: { row: EventHistoryItem }) => formatDate(row.createdAt),
      },
      {
        key: "createdBy",
        name: this.props.t("ACTIONED_BY"),
        renderCell: ({ row }: { row: EventHistoryItem }) =>
          row.taskType === "ALERT_TRIGGERED"
            ? this.props.t("N/A")
            : formatAuthoredBy(row.createdBy),
      },
      {
        key: "actions",
        name: "",
        renderCell: ({ row }: { row: EventHistoryItem }) =>
          row.taskType === "ALERT_TRIGGERED" ||
          row.description === this.props.t("TASK_NOT_COMPLETED_YET") ? null : (
            <span className="material-icons pointer" onClick={this.handleTaskClick(row)}>
              search
            </span>
          ),
      },
    ];
  }
  get data() {
    return this.historyList.filter((record: EventHistoryItem) => {
      return !(record.createdBy === null && record.taskType === "ABSENCE_MONITORING_REVIEW");
    });
  }

  render() {
    const lastReview = this.props.lastAbsenceReviewTask;
    return (
      <View>
        {this.props.showSummary && (
          <SectionComponent wrapperClassName="flex-column">
            <div className="d-flex justify-content-between activity-summary">
              <div className="min-width-20">
                <StatItem
                  title={this.props.t("CURRENT_LEVEL")}
                  value={
                    lastReview && lastReview.currentLevel
                      ? lastReview.currentLevel.name
                      : this.props.t("NOT_IN_PROCESS")
                  }
                  valueClass="font-size-20"
                />
              </div>
              <div className="min-width-60 px-md-3">
                <StatItem
                  title={this.props.t("DESCRIPTION")}
                  value={
                    lastReview && lastReview.currentLevel
                      ? lastReview.currentLevel.description
                      : this.props.t("N/A")
                  }
                  valueClass="font-size-20"
                />
              </div>
              <div className="min-width-20">
                <StatItem
                  title={this.props.t("NEXT_REVIEW_DATE")}
                  value={
                    lastReview && lastReview.nextReviewDate
                      ? formatDate(lastReview.nextReviewDate)
                      : this.props.t("N/A")
                  }
                  valueClass="font-size-20"
                />
              </div>
            </div>
          </SectionComponent>
        )}

        <SectionComponent
          wrapperClassName="flex-column"
          title={<h5>{this.props.gridSectionTitle || ""}</h5>}
          rightLink={
            this.props.enableMonitoringUpdate && (
              <div className="d-flex justify-content-end w-100">
                {this.props.currentEmployeeAccessLevel == "FULL_DETAILS" && (
                  <Button
                    materialIcon="rebase_edit"
                    disabled={!this.isValid}
                    onClick={this.toggleUpdateModal}
                  >
                    {this.props.t("UPDATE")}
                  </Button>
                )}
                {this.props.enableManualReferral && (
                  <Button
                    className="ml-2"
                    disabled={!this.props.isManualReferralAvailable}
                    onClick={this.props.toggleManualReferralModal}
                    materialIcon="add_box"
                  >
                    {this.props.t("ADD")}
                  </Button>
                )}
              </div>
            )
          }
        >
          <div className="d-flex justify-content-between mb-4 activity-history">
            <ButtonGroup className="d-flex w-100">
              {this.filters.length > 1 &&
                !this.props.hideFilters &&
                this.filters.map(filter => {
                  return (
                    <Button
                      key={filter}
                      size="small"
                      color={this.getButtonState(filter)}
                      onClick={this.getEventHistory(filter)}
                    >
                      {this.props.t(filter)}
                    </Button>
                  );
                })}
            </ButtonGroup>

            {this.props.enableMonitoringUpdate && this.props.showUpdateModal && (
              <MonitoringUpdateModal
                clientId={this.props.clientId}
                employeeId={this.props.employeeId}
                onCancel={this.toggleUpdateModal}
              />
            )}

            {this.props.enableManualReferral && this.props.showManualReferralModal && (
              <ManualReferralModal
                clientId={this.props.clientId}
                employeeId={this.props.employeeId}
                t={this.props.t}
              />
            )}

            {this.props.showManualReferralTaskData && (
              <ManualReferralTaskModal
                toggleModal={this.props.toggleManualReferralTaskDataModal}
                data={this.props.manualReferralTaskData}
                t={this.props.t}
              />
            )}

            {this.props.showHistoryModal && this.props.selectedRTWTask ? (
              <AbsenceRTWInterviewModal
                task={this.selectedRTWTask}
                onCancel={this.toggleHistoryModal()}
              />
            ) : null}
            {this.props.showAbsenceReviewModal && this.props.selectedAbsenceReviewTask ? (
              <AbsenceReviewViewModal
                task={this.props.selectedAbsenceReviewTask}
                onCancel={this.toggleAbsenceReviewTaskModal}
              />
            ) : null}
          </div>
          {this.props.showReferralDecisionModal && this.props.selectReferralDecisionTaskData && (
            <ReferralDecisionModal
              toggleModal={this.props.toggleReferralDecisionModal}
              data={this.props.selectReferralDecisionTaskData}
            />
          )}
          <GSGrid rows={this.data} columns={this.columns} className="mb-5" />
        </SectionComponent>
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  eventHistoryList: selectors.getEventHistoryList(state),
  lastAbsenceReviewTask: selectors.getLastAbsenceReviewTask(state),
  selectedAbsenceReviewTask: selectors.getSelectedAbsenceReviewTask(state),
  selectReferralDecisionTaskData: selectors.selectReferralDecisionTask(state),
  selectedRTWTask: getSelectedTaskHistory(state),
  showUpdateModal: selectors.showMonitoringUpdateModal(state),
  showManualReferralModal: selectors.showManualReferralModal(state),
  isManualReferralAvailable: selectors.isManualReferralAvailable(state),
  showHistoryModal: selectors.showHistoryItemModal(state),
  showAbsenceReviewModal: selectors.showAbsenceReviewModal(state),
  showReferralDecisionModal: selectors.showReferralDecisionModal(state),
  showManualReferralTaskData: selectors.showManualReferralTaskData(state),
  manualReferralTaskData: selectors.selectManualReferralTaskData(state),
  currentEmployeeAccessLevel: employeeSelectors.getCurrentEmployeeAccessLevel(state),
  task: selectors.getReviewTask(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getEventHistory: actionCreator.GetEmployeeEventHistory,
  resetHistory: actionCreator.ResetEmployeeEventHistory,
  updateReviewTask: actionCreator.GetLastAbsenceReviewTaskSuccess,
  getManualReferralEnabled: actionCreator.GetManualReferralEnabled,
  getReferralProcesses: actionCreator.GetReferralProcesses,
  getLastAbsenceReviewTask: actionCreator.GetLastAbsenceReviewTask,
  getAbsenceReviewTask: actionCreator.GetAbsenceReviewTask,
  getReferralDecisionTaskData: actionCreator.GetReferralDecisionTaskData,
  getManualReferralTaskData: actionCreator.GetManualReferralTaskData,
  getRtwInterviewTask: reportingActionCreator.GetRtwInterviewTask,
  setRtwInterviewTaskHistory: reportingActionCreator.SetRtwInterviewTaskHistory,
  toggleUpdateModal: actionCreator.ToggleMonitoringUpdateModal,
  toggleManualReferralModal: actionCreator.ToggleManualReferralModal,
  toggleHistoryModal: actionCreator.ToggleEventHistoryItemModal,
  toggleAbsenceReviewModal: actionCreator.ToggleAbsenceReviewModal,
  toggleReferralDecisionModal: actionCreator.ToggleReferralDecisionModal,
  toggleManualReferralTaskDataModal: actionCreator.ToggleManualReferralTaskDataModal,
  push,
};

export default compose(connect(mapStateToProps, mapDispatchToProps), localize())(EventHistory);
